import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, ListGroup, Image, Card, Button } from "react-bootstrap";
import { PaystackButton } from "react-paystack";
import { useDispatch, useSelector } from "react-redux";
import httpService from "../../services/httpService";
import Loader from "../common/Loader";
import Message from "../common/Message";
import Meta from "../common/Meta";
import { currencyFormat } from "../../utils/currencyFormat";
import { dateFormat } from "../../utils/dateFormat";
import {
  getOrderDetails,
  verifyOrder,
  payOrder,
  deliverOrder,
  cancelOrder,
} from "../../actions/orderActions";
import {
  ORDER_PAY_RESET,
  ORDER_DELIVER_RESET,
  ORDER_CANCEL_RESET,
} from "../../constants/orderConstants";

const OrderScreen = () => {
  const publicKey = process.env.REACT_APP_PAYPAL_PUBLIC_KEY;

  const { id: orderId } = useParams();
  const navigate = useNavigate();

  const [proceedButton, setProceedButton] = useState(false);

  const dispatch = useDispatch();

  const orderDetails = useSelector((state) => state.orderDetails);
  const { order, loading, error } = orderDetails;

  const orderVerify = useSelector((state) => state.orderVerify);
  const {
    loading: loadingVerify,
    error: errorVerify,
    success: successVerify,
  } = orderVerify;

  const orderCancel = useSelector((state) => state.orderCancel);
  const {
    loading: loadingCancel,
    success: successCancel,
    error: errorCancel,
  } = orderCancel;

  const orderPay = useSelector((state) => state.orderPay);
  const {
    loading: loadingPay,
    success: successPay,
    error: errorPay,
  } = orderPay;

  const orderDeliver = useSelector((state) => state.orderDeliver);
  const { loading: loadingDeliver, success: successDeliver } = orderDeliver;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const paystackProps = {
    email: userInfo ? userInfo.email : "",
    amount: order ? Number(order.totalPrice) * 100 : 0,
    metadata: {
      name: order ? order.deliveryAddress.fullName : "",
      phone: order ? order.deliveryAddress.phone : "",
    },
    publicKey,
    text: "Pay Now",
    onSuccess: (reference) => successPaymentHandler(reference),
    onClose: () => alert("Wait! You need to make payment, don't go!!!!"),
  };

  const fetchedOrderId = order ? order._id : "";

  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
    }

    // If no order has been fetched, fetch it or
    // if payment is successful fetch order details
    if (
      !order ||
      successPay ||
      successDeliver ||
      successCancel ||
      orderId !== order._id
    ) {
      dispatch({ type: ORDER_CANCEL_RESET });
      dispatch({ type: ORDER_PAY_RESET });
      dispatch({ type: ORDER_DELIVER_RESET });
      dispatch(getOrderDetails(orderId));

      // console.log(order);
    } else if (!order.isPaid) {
      setProceedButton(true);
    }
  }, [
    dispatch,
    userInfo,
    navigate,
    order,
    orderId,
    successCancel,
    successPay,
    successDeliver,
  ]);

  const verifyOrderHandler = () => {
    dispatch(verifyOrder(orderId));
    setProceedButton(false);
  };

  const cancelHandler = () => {
    dispatch(cancelOrder(orderId));
  };

  const successPaymentHandler = (reference) => {
    // console.log(reference);
    dispatch(payOrder(orderId, reference));
  };

  const deliverHandler = () => {
    dispatch(deliverOrder(order));
  };

  return (
    <>
      {userInfo &&
        (userInfo.isAdmin ? (
          <Meta title="Manage Order" />
        ) : (
          <Meta title="My Order" />
        ))}

      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <>
          <h1>Order {fetchedOrderId}</h1>
          <Row>
            <Col md={8}>
              <ListGroup variant="flush">
                {order?.isCanceled && (
                  <ListGroup.Item>
                    <Message variant="info">
                      This order has been canceled.
                    </Message>
                  </ListGroup.Item>
                )}
                <ListGroup.Item>
                  <h2>Delivery</h2>
                  <p>
                    <strong>Name: </strong> {order.deliveryAddress.fullName}
                  </p>
                  <p>
                    {" "}
                    <strong>Email: </strong>{" "}
                    <a href={`mailto:${userInfo.email}`} className="link">
                      {userInfo.email}
                    </a>
                  </p>
                  <p>
                    <strong>Phone: </strong> {order.deliveryAddress.phone}
                  </p>
                  <p>
                    <strong>Address:</strong>
                    {order?.deliveryAddress.address},{" "}
                    {order?.deliveryAddress.city}{" "}
                  </p>
                  {order.isDelivered ? (
                    <Message variant="success">
                      Delivered on {dateFormat(order.deliveredAt)}
                    </Message>
                  ) : (
                    <Message variant="danger">Not Delivered</Message>
                  )}
                </ListGroup.Item>

                <ListGroup.Item>
                  <h2>Payment Method</h2>
                  <p>
                    <strong>Method: </strong>
                    {order.paymentMethod}
                  </p>
                  {order.isPaid ? (
                    <>
                      <Message variant="success">
                        Paid on {dateFormat(order.paidAt)}
                      </Message>
                      <Message variant="success">
                        Status: {order.paymentResult.status.toUpperCase()}
                      </Message>
                      <Message variant="success">
                        Transaction reference: {order.paymentResult.trxref}
                      </Message>
                    </>
                  ) : (
                    <Message variant="danger">Not Paid</Message>
                  )}
                </ListGroup.Item>
                {order.isPaid && (
                  <ListGroup.Item>
                    <h2>Payment Details</h2>
                  </ListGroup.Item>
                )}

                <ListGroup.Item>
                  <h2>Order Items</h2>
                  {order.orderItems.length === 0 ? (
                    <Message>Order is empty</Message>
                  ) : (
                    <ListGroup variant="flush">
                      {order.orderItems.map((item, index) => (
                        <ListGroup.Item key={index}>
                          <Row>
                            <Col md={1}>
                              <Image
                                src={item.image}
                                alt={item.name}
                                fluid
                                rounded
                              />
                            </Col>
                            <Col>
                              <Link
                                to={`/item/${item.product}`}
                                className="link"
                              >
                                {item.name}
                              </Link>
                            </Col>
                            <Col md={4}>
                              {item.qty} x {currencyFormat(item.price)} ={" "}
                              {currencyFormat(
                                (item.qty * item.price).toFixed(2)
                              )}
                            </Col>
                          </Row>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Col>
            <Col md={4}>
              <Card>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <h2>Order Summary</h2>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row>
                      <Col>Items</Col>
                      <Col>{currencyFormat(order.itemsPrice)}</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row>
                      <Col>Delivery</Col>
                      <Col>{currencyFormat(order.deliveryPrice)}</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row>
                      <Col>Tax</Col>
                      <Col>{currencyFormat(order.taxPrice)}</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row>
                      <Col>Total</Col>
                      <Col>{currencyFormat(order.totalPrice)}</Col>
                    </Row>
                  </ListGroup.Item>

                  {!order.isPaid && !order.isCanceled && (
                    <ListGroup.Item>
                      {loadingCancel && <Loader />}
                      {errorCancel && (
                        <Message variant="danger">{errorCancel}</Message>
                      )}
                      <Button
                        type="button"
                        className="btn btn-block"
                        onClick={cancelHandler}
                        variant="primary"
                      >
                        Cancel Order
                      </Button>
                    </ListGroup.Item>
                  )}

                  {!order.isPaid && (
                    <ListGroup.Item>
                      {loadingVerify && <Loader />}
                      {errorVerify && (
                        <Message variant="danger">{errorVerify}</Message>
                      )}
                      {loadingPay && <Loader />}
                      {errorPay && (
                        <Message variant="danger">{errorPay}</Message>
                      )}

                      {proceedButton && (
                        <Button
                          type="button"
                          className="btn btn-block"
                          onClick={verifyOrderHandler}
                          variant="primary"
                        >
                          Make Payment
                        </Button>
                      )}

                      {successVerify && <PaystackButton {...paystackProps} />}
                    </ListGroup.Item>
                  )}
                  {loadingDeliver && <Loader />}
                  {userInfo &&
                    userInfo.isAdmin &&
                    order.isPaid &&
                    !order.isDelivered && (
                      <ListGroup.Item>
                        <Button
                          type="button"
                          className="btn btn-block"
                          onClick={deliverHandler}
                        >
                          Mark as Delivered
                        </Button>
                      </ListGroup.Item>
                    )}
                </ListGroup>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default OrderScreen;
